import React, { useState } from 'react';
import { DemandPlanningSettings } from '../../../types';
import { Settings, Save, RotateCcw } from 'lucide-react';

interface DemandSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentSettings: DemandPlanningSettings;
  onSave: (settings: DemandPlanningSettings) => Promise<void>;
}

export const DemandSettingsModal: React.FC<DemandSettingsModalProps> = ({
  isOpen,
  onClose,
  currentSettings,
  onSave,
}) => {
  const [minStudents, setMinStudents] = useState<number>(currentSettings.minStudentsForRoute);
  const [highDemand, setHighDemand] = useState<number>(currentSettings.highDemandThreshold);
  const [veryHighDemand, setVeryHighDemand] = useState<number>(
    currentSettings.veryHighDemandThreshold
  );
  const [vehicleCapacity, setVehicleCapacity] = useState<number>(
    currentSettings.vehicleCapacity
  );
  const [isSaving, setIsSaving] = useState(false);

  if (!isOpen) return null;

  const handleResetDefaults = () => {
    setMinStudents(10);
    setHighDemand(25);
    setVeryHighDemand(50);
    setVehicleCapacity(50);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSave({
        minStudentsForRoute: Number(minStudents),
        highDemandThreshold: Number(highDemand),
        veryHighDemandThreshold: Number(veryHighDemand),
        vehicleCapacity: Number(vehicleCapacity),
      });
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="demand-modal-overlay" onClick={onClose}>
      <div className="demand-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="demand-modal-header">
          <h3 className="demand-modal-title">
            <Settings size={20} className="text-blue-600" />
            <span>معايير التقييم التشغيلي لفتح الخطوط (Operational Thresholds)</span>
          </h3>
          <button className="demand-modal-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <p className="text-xs text-slate-500 mb-4 leading-relaxed">
          تحديد الحدود الرقمية التلقائية لحساب نقاط الجدوى التشغيلية وتصنيف الجامعات المؤهلة لتدشين رحلات جديدة.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="demand-form-grid">
            <div className="demand-form-group">
              <label className="demand-form-label">
                الحد الأدنى للطلاب لدراسة الخط (Min Students)
              </label>
              <input
                type="number"
                min={1}
                max={100}
                value={minStudents}
                onChange={(e) => setMinStudents(Number(e.target.value))}
                className="demand-form-input font-mono"
                required
              />
              <span className="demand-form-hint">القيمة الافتراضية: 10 طلاب</span>
            </div>

            <div className="demand-form-group">
              <label className="demand-form-label">
                عتبة الطلب المرتفع (High Demand Threshold)
              </label>
              <input
                type="number"
                min={minStudents}
                max={200}
                value={highDemand}
                onChange={(e) => setHighDemand(Number(e.target.value))}
                className="demand-form-input font-mono"
                required
              />
              <span className="demand-form-hint">القيمة الافتراضية: 25 طالباً</span>
            </div>

            <div className="demand-form-group">
              <label className="demand-form-label">
                عتبة الطلب المرتفع جداً (Very High Demand)
              </label>
              <input
                type="number"
                min={highDemand}
                max={500}
                value={veryHighDemand}
                onChange={(e) => setVeryHighDemand(Number(e.target.value))}
                className="demand-form-input font-mono"
                required
              />
              <span className="demand-form-hint">القيمة الافتراضية: 50 طالباً</span>
            </div>

            <div className="demand-form-group">
              <label className="demand-form-label">
                سعة المركبة التشغيلية القياسية (سعة الحافلة)
              </label>
              <input
                type="number"
                min={4}
                max={100}
                value={vehicleCapacity}
                onChange={(e) => setVehicleCapacity(Number(e.target.value))}
                className="demand-form-input font-mono"
                required
              />
              <span className="demand-form-hint">القيمة الافتراضية: 50 مقعداً</span>
            </div>
          </div>

          <div className="flex justify-between items-center pt-3 border-t">
            <button
              type="button"
              className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 font-semibold"
              onClick={handleResetDefaults}
            >
              <RotateCcw size={12} />
              <span>استعادة الإعدادات الافتراضية</span>
            </button>

            <div className="flex gap-2">
              <button type="button" className="btn-admin-outline text-xs" onClick={onClose}>
                إلغاء
              </button>
              <button
                type="submit"
                className="btn-primary text-xs font-bold"
                disabled={isSaving}
              >
                <Save size={13} />
                <span>{isSaving ? 'جارٍ الحفظ...' : 'حفظ المعايير'}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
